import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
 AppBar,
 Toolbar,
 IconButton,
 Button,
 Avatar,
 Box,
 CircularProgress,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import { useAuthentication, useRestaurantByUserId, useTheme } from "hooks";
import { API_ENDPOINTS } from "constants";
import { SideNavbar } from "./side";
import {
 getBusinessItems,
 getSignedInBusinessItems,
 getProfileItems,
 getAuthenticatedDrawerItems,
 getUnauthenticatedDrawerItems,
} from "constants";
import { NotificationButton, NavbarChatButton, NavbarOrdersButton } from "components";
import { NavbarCartButton } from "components/buttons";

export function TopNavbar() {
 const [drawerOpen, setDrawerOpen] = useState(false);
 const [profileExpanded, setProfileExpanded] = useState(false);
 const [businessExpanded, setBusinessExpanded] = useState(false);

 const { isAuthenticated, user, userFullName, loading, signIn, signOut } =
  useAuthentication();
 const userPhotoUrl = user?.photoUrl || null;

 const { themeMode, toggleTheme } = useTheme();
 const navigate = useNavigate();
 const location = useLocation();

 const { restaurant, loading: restaurantLoading } = useRestaurantByUserId(
  user?.uid,
 );

 const isOnSearchPage = location.pathname === "/search";

 const toggleDrawer = (open) => () => {
  setDrawerOpen(open);
  if (!open) {
   setProfileExpanded(false);
   setBusinessExpanded(false);
  }
 };

 const handleSignOut = async () => {
  setDrawerOpen(false);
  setProfileExpanded(false);
  setBusinessExpanded(false);
  await signOut();
  navigate("/", { replace: true });
 };

 const handleSignIn = () => {
  setDrawerOpen(false);
  signIn();
 };

 const handleNavigation = (path) => () => {
  setDrawerOpen(false);
  setProfileExpanded(false);
  setBusinessExpanded(false);

  if (path.startsWith("http://") || path.startsWith("https://")) {
   window.location.href = path;
  } else {
   navigate(path, { replace: true });
  }
 };

 const handleThemeToggle = (event) => {
  event.preventDefault();
  event.stopPropagation();
  toggleTheme();
 };

 const handleSearchClick = () => {
  navigate("/search", { replace: true });
 };

 const handleProfileToggle = () => {
  setProfileExpanded(!profileExpanded);
 };

 const handleBusinessToggle = () => {
  setBusinessExpanded(!businessExpanded);
 };

 const getInitials = (name) => {
  if (!name) return user?.email?.charAt(0)?.toUpperCase() || "U";
  return name
   .split(" ")
   .map((n) => n.charAt(0))
   .join("")
   .toUpperCase();
 };

 const businessItems =
  user && restaurant
   ? getSignedInBusinessItems(handleNavigation, API_ENDPOINTS, restaurant?.id)
   : getBusinessItems(handleNavigation, API_ENDPOINTS);
 const profileItems = getProfileItems(handleNavigation, API_ENDPOINTS);

 const authenticatedItems = getAuthenticatedDrawerItems({
  businessExpanded,
  handleBusinessToggle,
  businessItems,
  profileExpanded,
  handleProfileToggle,
  profileItems,
  themeMode,
  handleThemeToggle,
  handleSignOut,
 });

 const unauthenticatedItems = getUnauthenticatedDrawerItems({
  businessExpanded,
  handleBusinessToggle,
  businessItems,
  themeMode,
  handleThemeToggle,
 });

 return (
  <Box pb={8}>
   <AppBar position="fixed">
    <Toolbar>
     <Button
      component={Link}
      to="/"
      color="inherit"
      sx={{
       typography: "h6",
       textTransform: "none",
       px: 0,
       position: { xs: "static", md: "absolute" },
       left: { md: "50%" },
       transform: { md: "translateX(-50%)" },
       zIndex: 1,
      }}
      aria-label="Go to home"
     >
      {process.env.REACT_APP_NAME || "App"}
     </Button>

     <Box sx={{ flexGrow: 1 }} />

     {(loading || restaurantLoading) && (
      <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
     )}

     <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {!isOnSearchPage && (
       <IconButton
        color="inherit"
        onClick={handleSearchClick}
        size="small"
        aria-label="Search"
       >
        <SearchIcon />
       </IconButton>
      )}

      {isAuthenticated && (
       <>
        <NavbarCartButton />
        <NavbarOrdersButton />
        <NavbarChatButton />
        <NotificationButton />
       </>
      )}

      {isAuthenticated ? (
       <Avatar
        src={userPhotoUrl || undefined}
        sx={{
         width: 32,
         height: 32,
         cursor: "pointer",
         fontSize: "0.875rem",
         ml: 0.5,
        }}
        onClick={toggleDrawer(true)}
        alt={userFullName || user?.email}
       >
        {!userPhotoUrl && getInitials(userFullName)}
       </Avatar>
      ) : (
       <IconButton
        edge="end"
        color="inherit"
        aria-label="menu"
        onClick={toggleDrawer(true)}
        disabled={loading}
        size="small"
        sx={{ ml: 0.5 }}
       >
        <MenuIcon />
       </IconButton>
      )}
     </Box>
    </Toolbar>
   </AppBar>

   <SideNavbar
    drawerOpen={drawerOpen}
    toggleDrawer={toggleDrawer}
    isAuthenticated={isAuthenticated}
    user={user}
    userFullName={userFullName}
    getInitials={getInitials}
    loading={loading}
    handleSignIn={handleSignIn}
    authenticatedItems={authenticatedItems}
    unauthenticatedItems={unauthenticatedItems}
   />
  </Box>
 );
}
